import { Layout } from '@/components/layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Shield, Zap, AlertCircle } from 'lucide-react';
import { productsData } from '@/data/productsData';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const PreWorkoutPage = () => {
  const preworkoutProducts = productsData.filter(p => p.category === 'preworkout');

  return (
    <>
      <Helmet>
        <title>Buy Pre-Workout Supplements in India & Nepal – Energy & Focus | NutraFuel®</title>
        <meta name="description" content="Shop powerful pre-workout supplements in India & Nepal. Boost energy, focus & performance. 100% authentic C4, Cellucor & more. Fast delivery across India." />
        <meta name="keywords" content="buy pre workout india, pre workout supplements nepal, c4 pre workout india, best pre workout india, energy supplements" />
        <link rel="canonical" href="https://nutrafuel.in/pre-workout" />
        <meta property="og:title" content="Buy Pre-Workout Supplements in India & Nepal" />
        <meta property="og:description" content="Explosive energy and focus for intense training sessions." />
        <meta property="og:url" content="https://nutrafuel.in/pre-workout" />
      </Helmet>

      <Layout>
        {/* Breadcrumb */}
        <div className="bg-muted/30 py-3 border-b border-border">
          <div className="container mx-auto px-4">
            <nav className="text-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary transition-colors">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-foreground font-medium">Pre-Workout</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <section className="relative pt-32 md:pt-40 pb-16 bg-gradient-to-b from-muted/50 to-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <Badge className="mb-4 bg-gold text-foreground">Energy & Performance</Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight mb-6">
                Buy Pre-Workout Supplements in India & Nepal
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Unlock explosive energy, laser focus, and intense muscle pumps with authentic pre-workout supplements. Shop C4, Cellucor, and other top brands trusted by athletes across India and Nepal.
              </p>

              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 text-green-600">
                  <Shield className="w-4 h-4" />
                  <span className="text-sm font-medium">100% Authentic</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary">
                  <Zap className="w-4 h-4" />
                  <span className="text-sm font-medium">Maximum Energy</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Products Grid */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-display font-bold text-foreground mb-8">
              Premium Pre-Workout Supplements
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-16">
              {preworkoutProducts.map((product) => (
                <Link key={product.id} to={`/products/${product.slug}`}>
                  <Card className="group overflow-hidden border-0 shadow-soft hover:shadow-elevated transition-all duration-300 hover:-translate-y-2 flex flex-col h-full">
                    <div className="relative aspect-square overflow-hidden bg-muted">
                      <img
                        src={product.image}
                        alt={`${product.name} - Pre-workout supplement for energy and focus`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                      {product.popular && (
                        <Badge className="absolute top-3 left-3 bg-gold text-foreground">
                          Popular
                        </Badge>
                      )}
                      {product.stock === 'Limited Stock' && (
                        <Badge className="absolute bottom-3 left-3 bg-orange-500 text-white">
                          Limited Stock
                        </Badge>
                      )}
                    </div>
                    <CardContent className="p-5 flex flex-col flex-1">
                      <span className="text-xs text-gold font-medium uppercase tracking-wider mb-1">{product.brand}</span>
                      <h3 className="font-bold text-foreground mb-2">{product.name}</h3>
                      <p className="text-sm text-muted-foreground mb-4 flex-1">{product.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {product.features.map((feature) => (
                          <span key={feature} className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {/* SEO Content */}
            <div className="max-w-4xl mx-auto">
              <article className="bg-white rounded-lg p-8 shadow-sm">
                <h2 className="text-2xl font-display font-bold text-foreground mb-6">
                  What Are Pre-Workout Supplements?
                </h2>
                
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Pre-workout supplements are performance-enhancing formulas designed to boost energy, focus, endurance, and muscle pumps during training. Popular among gym-goers in India and Nepal, these supplements contain ingredients like caffeine, beta-alanine, citrulline, and B-vitamins to help you push harder and train longer.
                </p>

                <h2 className="text-2xl font-display font-bold text-foreground mb-6">
                  Benefits of Pre-Workout Supplements
                </h2>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div><strong>Explosive Energy:</strong> Caffeine and energy compounds fight fatigue and boost mental alertness for intense training sessions.</div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div><strong>Enhanced Focus:</strong> Nootropic ingredients improve mind-muscle connection and training concentration.</div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div><strong>Muscle Pumps:</strong> Nitric oxide boosters increase blood flow for skin-splitting muscle pumps and nutrient delivery.</div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div><strong>Increased Endurance:</strong> Beta-alanine buffers lactic acid, allowing you to train harder for longer periods.</div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div><strong>Better Performance:</strong> Studies show 5-15% improvement in strength and power output when using pre-workout.</div>
                  </li>
                </ul>

                <div className="bg-orange-50 border-l-4 border-orange-500 rounded p-6 mb-8">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-foreground mb-2">Important Safety Note:</h4>
                      <p className="text-sm text-muted-foreground">
                        Start with half dose to assess tolerance. Avoid taking pre-workout within 6 hours of bedtime. Not recommended for individuals sensitive to caffeine or with cardiovascular conditions. Always consult a doctor before starting any supplement.
                      </p>
                    </div>
                  </div>
                </div>

                {/* FAQ Section */}
                <h2 className="text-2xl font-display font-bold text-foreground mb-6">
                  Frequently Asked Questions About Pre-Workout
                </h2>

                <div className="space-y-6">
                  <div className="border-b border-border pb-6">
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      Is pre-workout safe for beginners in India?
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Yes, pre-workout is safe for beginners when used correctly. Start with half the recommended dose to assess caffeine tolerance. Choose products with 150-200mg caffeine (moderate range) rather than extreme formulas. All NutraFuel pre-workout products are authentic and safe when used as directed.
                    </p>
                  </div>

                  <div className="border-b border-border pb-6">
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      Pre-workout vs coffee: which is better?
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Pre-workout supplements offer more than just caffeine. They include performance enhancers like beta-alanine, citrulline, and B-vitamins that coffee doesn't provide. While coffee can provide energy, pre-workout delivers comprehensive performance benefits. Read our comparison: <Link to="/blog/pre-workout-vs-coffee" className="text-primary hover:underline">Pre-Workout vs Coffee</Link>.
                    </p>
                  </div>

                  <div className="border-b border-border pb-6">
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      When should I take pre-workout supplements?
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Take pre-workout 20-30 minutes before your training session for optimal results. This allows time for ingredients to be absorbed and start working. Avoid taking it too early (effects may wear off) or too late (may cause jitters without benefit).
                    </p>
                  </div>

                  <div className="pb-6">
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      Can I take pre-workout with creatine?
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Yes! Many athletes combine pre-workout with <Link to="/creatine" className="text-primary hover:underline">creatine monohydrate</Link> for maximum strength and performance. Some pre-workouts already contain creatine. If using both separately, take creatine post-workout or at a different time to avoid stomach discomfort.
                    </p>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default PreWorkoutPage;
